import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronUp, Search } from 'lucide-react';

/**
 * Компонент со списком и описанием HTTP-статусов
 */
const HttpStatusList = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [activeCategory, setActiveCategory] = useState('3xx');

  // Категории HTTP-статусов
  const statusCategories = [
    { id: '3xx', name: 'Перенаправления (3xx)', color: 'blue' },
    { id: '4xx', name: 'Клиентские ошибки (4xx)', color: 'orange' },
    { id: '5xx', name: 'Серверные ошибки (5xx)', color: 'red' }
  ];

  // Полный список статусов с описаниями
  const httpStatuses = {
    '3xx': [
      { code: 300, name: 'Multiple Choices', description: 'Запрос имеет несколько возможных ответов. Пользователь (или агент пользователя) должен выбрать один из них.', seoImpact: 'Минимальное влияние, очень редко встречается на практике.' },
      { code: 301, name: 'Moved Permanently', description: 'URL запрашиваемого ресурса был изменен навсегда. Новый URL указан в заголовке Location ответа.', seoImpact: 'Положительное влияние при правильном использовании. Передает большую часть ссылочного веса.' },
      { code: 302, name: 'Found', description: 'Ресурс временно доступен по другому URL. Изначально определялся как "Moved Temporarily".', seoImpact: 'Небольшое негативное влияние. Не передает ссылочную массу, сохраняет индексацию исходного URL.' },
      { code: 303, name: 'See Other', description: 'Сервер отправляет этот ответ, чтобы направить клиента за запрошенным ресурсом по другому URL с помощью GET-запроса.', seoImpact: 'Минимальное влияние, так как обычно используется для форм, а не для страниц, важных для SEO.' },
      { code: 304, name: 'Not Modified', description: 'Указывает, что содержимое не было изменено с момента предыдущего запроса. Используется для кэширования.', seoImpact: 'Положительное влияние, так как ускоряет загрузку страниц при повторных визитах.' },
      { code: 307, name: 'Temporary Redirect', description: 'Ресурс временно доступен по другому URL. В отличие от 302, гарантирует сохранение метода HTTP-запроса при перенаправлении.', seoImpact: 'Аналогично 302, не рекомендуется для постоянных изменений.' },
      { code: 308, name: 'Permanent Redirect', description: 'Ресурс был перемещен навсегда. В отличие от 301, гарантирует сохранение метода HTTP-запроса при перенаправлении.', seoImpact: 'Аналогично 301, очень хорошо для SEO при постоянных изменениях.' }
    ],
    '4xx': [
      { code: 400, name: 'Bad Request', description: 'Сервер не может или не будет обрабатывать запрос из-за ошибки, воспринимаемой как ошибка клиента.', seoImpact: 'Негативное влияние. Поисковые роботы не смогут проиндексировать страницу.' },
      { code: 401, name: 'Unauthorized', description: 'Требуется аутентификация. Клиент должен повторить запрос с соответствующими учетными данными.', seoImpact: 'Негативное влияние. Поисковые роботы обычно не могут пройти аутентификацию.' },
      { code: 403, name: 'Forbidden', description: 'Сервер понял запрос, но отказывается его авторизовать. Клиент не должен повторять запрос.', seoImpact: 'Негативное влияние. Страница не будет проиндексирована.' },
      { code: 404, name: 'Not Found', description: 'Сервер не может найти запрашиваемый ресурс. Часто это означает, что URL больше не существует.', seoImpact: 'Негативное влияние. Страницы с 404 удаляются из индекса. Большое количество 404 может негативно повлиять на сайт в целом.' },
      { code: 405, name: 'Method Not Allowed', description: 'Метод запроса известен серверу, но не поддерживается целевым ресурсом.', seoImpact: 'Негативное влияние. Поисковые роботы не смогут обработать страницу.' },
      { code: 410, name: 'Gone', description: 'Ресурс больше недоступен и не будет доступен в будущем. Клиенты не должны запрашивать этот ресурс повторно.', seoImpact: 'Лучше, чем 404 для удаленных страниц. Сигнализирует поисковикам, что страница намеренно удалена.' },
      { code: 429, name: 'Too Many Requests', description: 'Клиент отправил слишком много запросов за определенный промежуток времени.', seoImpact: 'Негативное влияние. Поисковый робот может прекратить сканирование сайта.' }
    ],
    '5xx': [
      { code: 500, name: 'Internal Server Error', description: 'Сервер столкнулся с ситуацией, которую он не знает, как обработать.', seoImpact: 'Сильное негативное влияние. Требует немедленного исправления.' },
      { code: 501, name: 'Not Implemented', description: 'Метод запроса не поддерживается сервером и не может быть обработан.', seoImpact: 'Негативное влияние. Поисковые роботы не смогут проиндексировать страницу.' },
      { code: 502, name: 'Bad Gateway', description: 'Сервер, действующий как шлюз, получил недопустимый ответ от вышестоящего сервера.', seoImpact: 'Сильное негативное влияние. Поисковые роботы могут временно прекратить индексацию.' },
      { code: 503, name: 'Service Unavailable', description: 'Сервер не готов обрабатывать запрос. Обычно это временное состояние.', seoImpact: 'Временное негативное влияние. При длительном состоянии страницы удаляются из индекса.' },
      { code: 504, name: 'Gateway Timeout', description: 'Сервер, действующий как шлюз, не получил своевременного ответа от вышестоящего сервера.', seoImpact: 'Негативное влияние. Аналогично 502.' }
    ]
  };

  // Фильтрация статусов
  const filteredStatuses = httpStatuses[activeCategory].filter(status => {
    if (!filter) return true;
    return (
      status.code.toString().includes(filter) || 
      status.name.toLowerCase().includes(filter.toLowerCase())
    );
  });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-8">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center w-full justify-between text-lg font-medium text-gray-900 dark:text-white mb-2"
      >
        <div className="flex items-center">
          <FileText className="w-5 h-5 mr-2 text-indigo-500" />
          Справочник HTTP-статусов
        </div>
        <div className="text-indigo-500">
          {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </button>
      
      {isOpen && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Справочник по кодам состояний HTTP с их описанием и влиянием на SEO.
          </p>
          
          {/* Поиск статусов */}
          <div className="mb-4 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="form-input pl-10"
              placeholder="Поиск по коду или названию статуса"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          
          {/* Категории статусов */}
          <div className="flex mb-4 border-b border-gray-200 dark:border-gray-700">
            {statusCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`mr-4 py-2 px-1 text-sm font-medium border-b-2 ${
                  activeCategory === category.id
                    ? `border-${category.color}-500 text-${category.color}-600 dark:text-${category.color}-400`
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
          
          {/* Список статусов */}
          {filteredStatuses.length > 0 ? (
            <div className="space-y-3 max-h-96 overflow-y-auto pr-1 custom-scrollbar">
              {filteredStatuses.map((status) => {
                // Определяем цвет в зависимости от категории статуса
                let statusColor = 'gray';
                if (status.code >= 300 && status.code < 400) statusColor = 'blue';
                else if (status.code >= 400 && status.code < 500) statusColor = 'orange';
                else if (status.code >= 500) statusColor = 'red';
                
                return (
                  <div 
                    key={status.code}
                    className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30"
                  >
                    <div className="flex items-center mb-2">
                      <div className={`px-2 py-1 rounded-md bg-${statusColor}-100 dark:bg-${statusColor}-900/20 text-${statusColor}-700 dark:text-${statusColor}-400 text-sm font-medium`}>
                        {status.code}
                      </div>
                      <h4 className="ml-2 font-medium text-gray-900 dark:text-white">
                        {status.name}
                      </h4>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {status.description}
                    </p>
                    <div className="mt-2">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        Влияние на SEO:
                      </span>
                      <span className="text-xs text-gray-600 dark:text-gray-400 ml-1">
                        {status.seoImpact}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              Статусы не найдены. Попробуйте изменить поисковый запрос.
            </div>
          )}
          
          {/* Российская специфика */}
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
              Особенности HTTP-статусов в Рунете
            </h4>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p>
                В российском сегменте интернета наблюдаются некоторые отличия в обработке HTTP-статусов:
              </p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Яндекс более чувствителен к типам редиректов и рекомендует использовать 301 для постоянных изменений</li>
                <li>Для региональных сайтов важно правильно настраивать перенаправления между региональными версиями</li>
                <li>При смене доменной зоны (например, с .ru на .рф) особенно важно корректно настроить 301-редиректы</li>
                <li>При технических работах рекомендуется настраивать код 503 с заголовком Retry-After, чтобы роботы Яндекса и Google знали, когда вернуться</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HttpStatusList;